import { makeStyles } from '@material-ui/core'
import useAppView from 'Core/Hooks/useAppView'
import { LICENSE_VIEWS, licenseRouteSegment } from 'Core/Hooks/useLicense/constants'
import { useLicense } from 'Core/Hooks/useLicense'
import { SideMenuItem } from 'procyon-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSignOutAlt } from '@fortawesome/sharp-solid-svg-icons'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { getAdminViewSidebarItems } from './adminMenuItems'
import { getUserViewRoutes } from './userMenuItems'
import { useUser } from 'Core/Hooks/useUser'
import classNames from 'classnames'
import useHTMLBodyScrollbar from 'Core/Hooks/useHTMLBodyScrollbar'
import { reduxApiClient } from 'infra'
import { useLayoutContext } from 'features/layout'

const STORAGE_KEY = 'sidemenu-collapsed-sections'

const SECTION_ICONS = {
  directory: 'folder_shared',
  cloud: 'cloud_circle',
  'Identity analytics': 'analytics',
  'Access Management': 'shield_lock',
  'Permission Management': 'manage_accounts',
  'AI Security': 'smart_toy',
  Secrets: 'lock',
  Activity: 'monitoring',
  Connections: 'hub'
}

const ALL_SECTIONS = [
  'directory',
  'cloud',
  'Identity analytics',
  'Access Management',
  'Permission Management',
  'AI Security',
  'Secrets',
  'Activity',
  'Connections'
]

const loadCollapsed = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === null) return new Set(ALL_SECTIONS)
    return new Set(JSON.parse(stored))
  } catch {
    return new Set(ALL_SECTIONS)
  }
}

// Animates children open/closed via max-height
const SectionItems = ({ open, children }) => {
  const ref = useRef(null)
  const [maxHeight, setMaxHeight] = useState(open ? 2000 : 0)

  useEffect(() => {
    if (open) {
      const h = ref.current ? ref.current.scrollHeight : 2000
      setMaxHeight(h + 20)
    } else {
      setMaxHeight(0)
    }
  }, [open])

  return (
    <div
      ref={ref}
      style={{
        maxHeight: `${maxHeight}px`,
        overflow: 'hidden',
        transition: 'max-height 0.25s ease'
      }}
    >
      {children}
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  root: {},
  sideMenuWrapper: {
    paddingTop: theme.spacing(4),
    height: '100%',
    position: 'fixed',
    borderRight: '1px solid var(--color-common-border)',
    zIndex: 1000,
    backgroundColor: 'var(--color-common-bg-base)'
  },
  overflow: {}
}))

function Sidemenu({ onSideMenuStateChange, sideMenuState }) {
  const classes = useStyles()
  const history = useHistory()
  const { whoAmI } = useLayoutContext()
  const [pathname, setPathname] = useState(null)
  const [collapsed, setCollapsed] = useState(true)
  const [keepOpen, setKeepOpen] = useState(false)
  const [collapsedSections, setCollapsedSections] = useState(loadCollapsed)
  const leaveTimer = useRef(null)
  const { viewMode } = useAppView()
  const { isAuditor, currentUserIsAdmin, isAgentlessMode, isMFAEnabled } = useUser()

  useEffect(() => {
    reduxApiClient('tenantprofiles').getAll({})
  }, [])

  const toggleSection = useCallback((sectionLabel) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionLabel)) {
        next.delete(sectionLabel)
      } else {
        next.add(sectionLabel)
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]))
      return next
    })
  }, [])

  const IsIdaVisible = false
  const IsShadowAIVisible = whoAmI?.enableshadowai
  // licensed view — hides nav items not part of the edition and applies
  // label overrides (e.g. Proxy Management → Gateway Management)
  const { licenseMode } = useLicense()

  const parsedItems = useMemo(() => {
    const items = []
    const unParsedItems =
      viewMode === 'admin'
        ? getAdminViewSidebarItems({ history, isMFAEnabled })
        : getUserViewRoutes({
            history,
            showSessionLogs: currentUserIsAdmin || isAuditor,
            isMFAEnabled
          })
    const view = LICENSE_VIEWS[licenseMode]
    let selectedFlag = false
    unParsedItems.forEach((e) => {
      if (!e) return
      if (isAgentlessMode && e._meta?.hideOnAgentless) return
      if (e._meta?.showIDA && !IsIdaVisible) return
      if (e._meta?.showShadowAI && !IsShadowAIVisible) return
      if (viewMode === 'admin') {
        const segment = licenseRouteSegment(e._meta?.itemActiveRoutes)
        if (segment && view?.hiddenRoutes?.includes(segment)) return
      }

      let selected = false
      if (!selectedFlag && e._meta && e._meta.itemActiveRoutes) {
        if (pathname && pathname.startsWith(e._meta.itemActiveRoutes)) {
          selected = true
          selectedFlag = true
        }
      }
      const { _meta, ...rest } = e
      const label = view?.labels?.[licenseRouteSegment(_meta?.itemActiveRoutes)] || e.label
      items.push({ ...rest, label, selected })
    })
    return items
  }, [viewMode, pathname, isAuditor, isAgentlessMode, IsIdaVisible, IsShadowAIVisible, licenseMode])

  // Group items by section label
  const menuGroups = useMemo(() => {
    const groups = []
    let current = { sectionLabel: null, items: [] }
    parsedItems.forEach((item) => {
      if (item.itemType === 'section') {
        groups.push({ ...current })
        current = { sectionLabel: item.label, items: [] }
      } else {
        current.items.push(item)
      }
    })
    groups.push({ ...current })
    return groups.filter((g) => g.items.length > 0)
  }, [parsedItems])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      setPathname(location.pathname)
    })
    return unlisten
  }, [])

  useEffect(() => {
    if (sideMenuState !== keepOpen) onSideMenuStateChange && onSideMenuStateChange(keepOpen)
  }, [sideMenuState, keepOpen])

  useHTMLBodyScrollbar(!collapsed)

  const isMenuCollapsed = collapsed && !keepOpen

  return (
    <div className={classes.root}>
      <div
        className={classes.sideMenuWrapper}
        onMouseEnter={() => {
          if (leaveTimer.current) {
            clearTimeout(leaveTimer.current)
            leaveTimer.current = null
          }
          setCollapsed(false)
        }}
        onMouseLeave={() => {
          leaveTimer.current = setTimeout(() => setCollapsed(true), 100)
        }}
      >
        <div
          style={{
            width: isMenuCollapsed ? '56px' : '289px',
            height: '100%',
            paddingTop: '30px',
            paddingLeft: '8px',
            paddingRight: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            transition: 'width 0.2s ease',
            overflow: 'hidden'
          }}
        >
          <div style={{ width: '100%', flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: '8px' }}>
            {menuGroups.map((group, gi) => {
              const isSectionCollapsed =
                !!group.sectionLabel && collapsedSections.has(group.sectionLabel)

              if (isMenuCollapsed) {
                // Icon-only mode: one section icon per group (no item icons)
                return (
                  <div key={gi}>
                    {gi > 0 && (
                      <div
                        style={{
                          height: '1px',
                          backgroundColor: 'var(--color-common-border)',
                          margin: '4px 6px'
                        }}
                      />
                    )}
                    {group.sectionLabel ? (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '36px',
                          cursor: 'default'
                        }}
                      >
                        <span
                          className='material-symbols-outlined'
                          style={{ fontSize: '20px', color: '#9ca3af' }}
                        >
                          {SECTION_ICONS[group.sectionLabel] || 'folder'}
                        </span>
                      </div>
                    ) : (
                      // ungrouped items (Dashboard) — show normally
                      group.items.map((item, ii) => (
                        <SideMenuItem
                          key={ii}
                          CustomIcon={item.CustomIcon}
                          icon={item.icon}
                          variant={item.variant || 'primary'}
                          disabled={item.disabled}
                          collapsed={true}
                          selected={item.selected}
                          label={item.label}
                          onClick={item.onClick}
                          keepExtended={false}
                        />
                      ))
                    )}
                  </div>
                )
              }

              // Expanded mode: full header + collapsible items
              return (
                <div key={gi}>
                  {group.sectionLabel ? (
                    <button
                      onClick={() => toggleSection(group.sectionLabel)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '266px',
                        height: '34px',
                        paddingLeft: '15px',
                        paddingRight: '15px',
                        marginTop: gi > 0 ? '6px' : '4px',
                        borderRadius: '5px',
                        border: 'none',
                        backgroundColor: 'var(--color-common-bg-secondary)',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ width: '24px', textAlign: 'center' }}>
                          <span
                            className='material-symbols-outlined'
                            style={{ fontSize: '18px', color: '#374151' }}
                          >
                            {SECTION_ICONS[group.sectionLabel] || 'folder'}
                          </span>
                        </div>
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            textTransform: 'capitalize',
                            letterSpacing: 'normal',
                            color: '#374151',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {group.sectionLabel}
                        </span>
                      </div>
                      <span
                        className='material-symbols-outlined'
                        style={{
                          fontSize: '16px',
                          color: '#6b7280',
                          transition: 'transform 0.2s',
                          transform: isSectionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                          flexShrink: 0
                        }}
                      >
                        expand_more
                      </span>
                    </button>
                  ) : null}

                  <SectionItems open={!isSectionCollapsed}>
                    <div style={{ paddingLeft: group.sectionLabel ? '12px' : '0' }}>
                      {group.items.map((item, ii) => (
                        <SideMenuItem
                          key={ii}
                          CustomIcon={item.CustomIcon}
                          icon={item.icon}
                          variant={item.variant || 'primary'}
                          disabled={item.disabled}
                          collapsed={false}
                          selected={item.selected}
                          label={item.label}
                          onClick={item.onClick}
                          keepExtended={keepOpen}
                          width={group.sectionLabel ? '254px' : '266px'}
                        />
                      ))}
                    </div>
                  </SectionItems>
                </div>
              )
            })}
          </div>

          {/* Keep-open toggle button */}
          <button
            onClick={() => setKeepOpen((s) => !s)}
            style={{
              backgroundColor: '#fff',
              border: 'none',
              borderTop: '1px solid #E3E3E3',
              color: keepOpen ? '#616161' : '#b0bec5',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              paddingTop: '13px',
              paddingLeft: '15px',
              paddingRight: '15px',
              width: isMenuCollapsed ? '46px' : '266px',
              cursor: 'pointer'
            }}
          >
            <FontAwesomeIcon
              icon={faSignOutAlt}
              className={keepOpen ? 'fa-flip-horizontal' : undefined}
            />
          </button>
        </div>
      </div>
    </div>
  )
}

export { Sidemenu }
